export class MapAnimation {
    // Start and stop properties
    private deltaRadius: number;
    private deltaOpacity: number;
    private startRadius: number;
    private startOpacity: number;

    // Animation properties
    private opacity;
    private radius;
    private duration;
    private easeFunction = MapAnimation.easeSimple;
    private startAnimTime = 0;

    // Animation layer specification
    private animationLayer: any;
    private static animationLayerCount: number = 0;

    // Callback on animation frame
    private onAnimCallback = null;

    // Is animation running
    private run = false;

    // Filter feature animation
    private filterProperty: string;
    private filterValue: string

    constructor(private map: any, private layer: any, initPaintOptions = {}) {
        // Add a new animation layer to the map
        this.animationLayer = {
            "id": "animation-layer" + MapAnimation.animationLayerCount++,
            "type": layer.type,
            "source": layer.source,
            //"source-layer": layer.id,
            "paint": Object.assign(layer.paint, initPaintOptions)
        };
        this.map.addLayer(this.animationLayer, layer.id);
    }

    /**
     * Destructor, should be called when animation is no longer in use
     **/
    destroy() {
        this.map.removeLayer(this.animationLayer.id);
    }

    /**
     * Start the map animation
     **/
    public start(endRadius: number, duration: number = 500, endOpacity: number = 1, delay: number = 0, easeFunction: any = MapAnimation.easeSimple): Promise<boolean> {
        // Init animation properties
        this.opacity = this.startOpacity = this.animationLayer.paint['circle-opacity'] || 1;
        this.radius = this.startRadius = this.animationLayer.paint['circle-radius'] || 20;
        this.easeFunction = easeFunction;

        if(endOpacity > 1 || endOpacity < 0) {
            console.error("End opacity must be between 0 and 1 but is " + endOpacity);
            endOpacity = 1;
        }

        this.duration = duration;
        // Init delta which needs to be animated
        this.deltaRadius = (endRadius - this.startRadius);
        this.deltaOpacity = (endOpacity - this.startOpacity);


        // Init the filter
        if(this.filterProperty) {
            this.map.setFilter(this.animationLayer.id, ["==", this.filterProperty, this.filterValue]);
            this.map.setFilter(this.layer.id, ["!=", this.filterProperty, this.filterValue]);
        }
        // Start the animation
        this.run = true;

        return new Promise((animationDone) => {
            setTimeout(() => {
                // Call first animation frame
                requestAnimationFrame((timestamp) => this.animateFeature(timestamp, animationDone));
            }, delay);
        });
    }

    /**
     *  Stop the map animation
     **/
    public stop() {
        this.run = false;
        this.onAnimFrame = null;
        this.startAnimTime = null;
        this.duration = null;

        // Save new paint options in layer
        this.animationLayer.paint['circle-opacity'] = this.opacity;
        this.animationLayer.paint['circle-radius'] = this.radius;

        // Destroy the filters
        if(this.filterProperty) {
            this.map.setFilter(this.animationLayer.id, ["==", this.filterProperty, ""]);
            this.map.setFilter(this.layer.id, ["!=", this.filterProperty, ""]);
        }
    }

    /**
    * Restrict the animation to run on only filtered features
    **/
    public setAnimationFilter(filterProperty: string, filterValue: string) {
        this.filterProperty = filterProperty;
        this.filterValue = filterValue;
    }

    /**
     * Register a callback functon on animation
     * Could be handy if we wanna change a property during animation or stop it on a condition
     **/
    public onAnimFrame(callback: any) {
        this.onAnimCallback = callback;
    }

    private animateFeature(timestamp: number, animationDone: any) {
        let animTime = 0;

        // Save first timestamp
        if(this.startAnimTime <= 0) {
            this.startAnimTime = timestamp;
        }
        else {
            // Calculate duration in ms (convert from micro to milli sec
            animTime = (timestamp - this.startAnimTime);
        }

        // Calculate new opacity and radius
        let pct = this.easeFunction(animTime / this.duration);
        this.radius = (this.deltaRadius * pct) + this.startRadius;

        pct = MapAnimation.easeSimple(animTime / this.duration);
        this.opacity = (this.deltaOpacity * pct) + this.startOpacity;

        // Redraw map
        this.opacity = (this.opacity >= 0 && this.opacity <= 1) ? this.opacity : this.startOpacity;
        this.map.setPaintProperty(this.animationLayer.id, 'circle-opacity', this.opacity);
        this.radius = (this.radius > 0) ? this.radius : this.startRadius;
        this.map.setPaintProperty(this.animationLayer.id, 'circle-radius', this.radius);

        // Animation is done
        if(animTime >= this.duration) {
            // End animation loop
            console.log(this.animationLayer.paint);
            this.stop();
            animationDone();
        }

        if(this.onAnimCallback) {
            this.onAnimCallback.apply(timestamp);
        }

        // Call this function again just before next vsync
        if(this.run) requestAnimationFrame((timestamp) => this.animateFeature(timestamp, animationDone));
    }

    public static easeSimple(pct): number {
        return (Math.sin(pct * Math.PI - 0.5 * Math.PI) + 1) * 0.5;
    }

    public static easeInCubic(pct): number {
        return Math.pow(pct, 3);
    }

    public static easeOutElastic(pct): number {
        return Math.pow(2, -10 * pct) * Math.sin((pct - 0.1 / 4) * (2 * Math.PI) / 0.1) + 1;
    }
}
